import { useMemo } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'
import { parseUnits } from 'viem'

import { SwapQuote } from '~/components/forms/swap/hooks/useSwap'
import { SwapFormValues } from '~/components/forms/swap/swapSchema'
import {
  SwapDataFormValues,
  TransferFormValues,
} from '~/components/forms/transfer/transferSchema'
import { TokenAmountFormValues } from '~/components/smart-accounts/schemas'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { IAddress } from '~/types'

type FormType = SwapFormValues | TransferFormValues

/**
 * This hook is used to get the SwapQuote from the context of a Swap form or Transfer form
 */
export const useSwapQuoteForm = (): SwapQuote | null => {
  const methods = useFormContext<FormType>()
  const { control } = methods

  // SwapFormValues fields
  const sourceToken = useWatch({ control, name: 'sourceToken' }) as
    TokenAmountFormValues | undefined
  const destinationToken = useWatch({ control, name: 'destinationToken' }) as
    TokenAmountFormValues | undefined
  const routerType = useWatch({ control, name: 'routerType' })
  const providerTransactionId = useWatch({
    control,
    name: 'providerTransactionId',
  })

  // TransferFormValues fields
  const swapData = useWatch({ control, name: 'swapData' }) as
    SwapDataFormValues | undefined
  const transferToken = useWatch({ control, name: 'transferToken' })

  // Shared fields
  const calls = useWatch({ control, name: 'calls' })

  // Token metadata
  const sourceTokenMetadata = useTokenMetadata({
    address: sourceToken?.token || swapData?.sellToken?.token,
    chainId: sourceToken?.chainId || swapData?.sellToken?.chainId,
    includePrice: true,
  })
  const destinationTokenMetadata = useTokenMetadata({
    address: destinationToken?.token || transferToken?.token,
    chainId: destinationToken?.chainId || transferToken?.chainId,
    includePrice: true,
  })

  return useMemo((): SwapQuote | null => {
    // Determine if this is TransferFormValues (has swapData) or SwapFormValues
    const isTransferForm = !!swapData

    if (isTransferForm) {
      if (
        !swapData?.sellToken?.amount ||
        !transferToken?.amount ||
        !swapData?.swapRouter
      ) {
        return null
      }

      return {
        chainId: swapData.sellToken.chainId,
        destinationChainId: transferToken.chainId,
        sellToken: swapData.sellToken.token as IAddress,
        sellAmount: parseUnits(
          swapData.sellToken.amount,
          sourceTokenMetadata?.decimals ?? 18,
        ),
        buyToken: transferToken.token as IAddress,
        buyAmount: parseUnits(
          transferToken.amount,
          destinationTokenMetadata?.decimals ?? 18,
        ),
        sender: swapData.sellToken.token,
        receiver: transferToken.token,
        routerType: swapData.swapRouter,
        calls: calls,
        providerTransactionId: swapData.providerTransactionId,
      }
    } else {
      if (!sourceToken?.amount || !destinationToken?.amount || !routerType) {
        return null
      }

      return {
        chainId: sourceToken.chainId,
        destinationChainId: destinationToken.chainId,
        sellToken: sourceToken.token,
        sellAmount: parseUnits(
          sourceToken.amount,
          sourceTokenMetadata?.decimals ?? 18,
        ),
        buyToken: destinationToken.token,
        buyAmount: parseUnits(
          destinationToken.amount,
          destinationTokenMetadata?.decimals ?? 18,
        ),
        sender: sourceToken.token,
        receiver: destinationToken.token,
        routerType: routerType,
        calls: calls,
        providerTransactionId: providerTransactionId,
      }
    }
  }, [
    swapData,
    transferToken,
    sourceTokenMetadata?.decimals,
    destinationTokenMetadata?.decimals,
    calls,
    sourceToken,
    destinationToken,
    routerType,
    providerTransactionId,
  ])
}
